import React from 'react';
import { styled, Box, Grid } from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { AppSimpleSelect, FieldRow } from 'components/FormElements';
import Spacing from 'components/Spacing';

const TableContainer = styled('table')(({ theme }) => ({
  display: 'table',
  tableLayout: 'fixed',
  width: '100%',
  fontSize: theme.fontSizes?.sm,
  color: theme.palette.text.darkBodyText,
  textAlign: 'left',
  borderCollapse: 'collapse',
}));

const TableHeading = styled('th')(({ theme }) => ({
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  verticalAlign: 'top',
}));

const TableCell = styled('td', {
  shouldForwardProp: (prop) => prop !== 'isPending',
})<{ isPending?: boolean }>(({ theme, isPending }) => ({
  paddingRight: theme.spacing(2),
  color: isPending ? theme.palette.grey[500] : 'inherit',
}));

const FooterRow = styled('tr')(({ theme }) => ({
  color: theme.palette.text.darkTitle,
  fontSize: theme.fontSizes?.regular,
  fontWeight: 'bold',
}));

const FooterCell = styled('td')<{ fontWeight?: string }>(
  ({ theme, fontWeight }) => ({
    paddingRight: theme.spacing(2),
    fontWeight,
  }),
);

export type ColumnConfig = {
  header: string;
  width?: string;
  renderCell?: (row: Record<string, any>, rowIndex: number) => React.ReactNode;
  renderFooter?: () => React.ReactNode;
};

export type EqualisationsAllocationsTableProps = {
  columns: Array<ColumnConfig>;
  rows: Array<Record<string, any>>;
  footerLabel?: string;
  isPending?: boolean;
  showAllocationRule?: boolean;
  allocationRule?: AllocationRule;
  allocationRuleOptions?: Array<{ value: AllocationRule; label: string }>;
  onChangeAllocationRule?: (value: AllocationRule) => void;
  isNotEditable?: boolean;
};

const EqualisationsAllocationsTable: React.FC<
  EqualisationsAllocationsTableProps
> = ({
  columns,
  rows,
  footerLabel,
  isPending = false,
  showAllocationRule = true,
  allocationRule,
  allocationRuleOptions = [],
  onChangeAllocationRule,
  isNotEditable = false,
}) => {
  return (
    <Spacing>
      {showAllocationRule && (
        <Grid size={{ sm: 12, md: 12, lg: 8 }}>
          <FieldRow centerTitle title="Allocation rule">
            <AppSimpleSelect
              value={allocationRule}
              fullWidth
              disabled={isNotEditable}
              options={allocationRuleOptions}
              onChange={(e) => {
                const value = e.target.value as AllocationRule;
                onChangeAllocationRule?.(value);
              }}
            />
          </FieldRow>
        </Grid>
      )}
      <Box>
        <TableContainer>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <TableHeading key={index}>{column.header}</TableHeading>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row.key || rowIndex}>
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex} isPending={isPending}>
                    {column.renderCell?.(row, rowIndex)}
                  </TableCell>
                ))}
              </tr>
            ))}
          </tbody>
        </TableContainer>

        <TableContainer>
          <tbody>
            <FooterRow>
              <FooterCell>
                {footerLabel || `${rows.length} investors`}
              </FooterCell>
              {columns.slice(1).map((column, index) => (
                <FooterCell key={index}>{column.renderFooter?.()}</FooterCell>
              ))}
            </FooterRow>
          </tbody>
        </TableContainer>
      </Box>
    </Spacing>
  );
};

export default EqualisationsAllocationsTable;
